import {MaterialModal, MaterialModalOptions} from "../material-modal";
import SignaturePad from "signature_pad";

export class SignatureModal extends MaterialModal {
    private _signaturePad: SignaturePad | undefined;
    private _resolve?: (value: string) => void;
    private _reject?: (reason?: any) => void;
    private static _instances: {[key: string]: SignatureModal} = {};

    // Store bound event handlers as instance properties
    private _boundHandleClear: (() => void) | undefined;
    private _boundHandleSave: (() => void) | undefined;
    private _boundHandleClose: (() => void) | undefined;

    protected initialize(modal: HTMLDivElement | string | undefined, options: MaterialModalOptions) {
        super.initialize(modal, options);
        this._boundHandleClear = this.handleClear.bind(this);
        this._boundHandleSave = this.handleSave.bind(this);
        this._boundHandleClose = this.handleClose.bind(this);
        this._signaturePad = new SignaturePad(this.getModal().querySelector('canvas') as HTMLCanvasElement);

        // Get references to buttons
        const clearBtn = this.getModal().querySelector('.modal-footer a:nth-child(2)');
        const saveBtn = this.getModal().querySelector('.modal-footer a:nth-child(3)');
        const closeBtn = this.getModal().querySelector('.modal-close');

        // Add event listeners once
        clearBtn?.addEventListener('click', this._boundHandleClear);
        saveBtn?.addEventListener('click', this._boundHandleSave);
        closeBtn?.addEventListener('click', this._boundHandleClose);

        // Handle modal background close
        this.getModal().addEventListener('modal-close', this._boundHandleClose);
    }

    set initialSignature(dataUrl: string) {
        this._signaturePad?.fromDataURL(dataUrl);
    }

    public destroy(): void {
        const id = this.getModal().id;

        // Remove event listeners
        const clearBtn = this.getModal().querySelector('.modal-footer a:nth-child(2)');
        const saveBtn = this.getModal().querySelector('.modal-footer a:nth-child(3)');
        const closeBtn = this.getModal().querySelector('.modal-close');

        clearBtn?.removeEventListener('click', this._boundHandleClear as EventListener);
        saveBtn?.removeEventListener('click', this._boundHandleSave as EventListener);
        closeBtn?.removeEventListener('click', this._boundHandleClose as EventListener);
        this.getModal().removeEventListener('modal-close', this._boundHandleClose as EventListener);

        // Cleanup signature pad
        this._signaturePad?.clear();
        this.getModal().remove();
        delete SignatureModal._instances[id];
    }

    private handleClear() {
        this._signaturePad?.clear();
    }

    private handleSave() {
        if (this._resolve) {
            this._resolve(this._signaturePad?.toDataURL() ?? '');
            this._resolve = undefined;
            this._reject = undefined;
        }
        this.close();
    }

    private handleClose() {
        if (this._reject) {
            this._reject(new Error('Signature modal closed without saving'));
            this._resolve = undefined;
            this._reject = undefined;
        }
    }

    get sign(): Promise<string> {
        // Clear any previous promise state
        if (this._reject) {
            this._reject(new Error('New signature request occurred'));
        }

        super.open();
        return new Promise<string>((resolve, reject) => {
            this._resolve = resolve;
            this._reject = reject;
            this._signaturePad?.clear(); // Reset pad on new request
        });
    }

    public static Init(id: string, imageURL?: string): SignatureModal {
        if (SignatureModal._instances[id]) {
            if(imageURL) SignatureModal._instances[id]._signaturePad?.fromDataURL(imageURL)
            return SignatureModal._instances[id]!;
        }

        const modal = document.createElement('div');
        modal.classList.add('modal', 'signature-modal');
        modal.id = id;
        modal.innerHTML = `
            <div class="modal-content">
                <h4>Your Signature</h4>
                <div class="signature-canvas-container">
                    <canvas width="400" height="200" class="signature-pad-ink"></canvas>
                </div>
                <p class="signature-hint">Please sign within the dotted area</p>
            </div>
            <div class="modal-footer">
                <a href="#!" class="modal-close waves-effect btn">Close</a>
                <a href="#!" class="waves-effect btn">Clear</a>
                <a href="#!" class="waves-effect btn apply-btn">Save Signature</a>
            </div>
        `;

        document.body.appendChild(modal);
        SignatureModal._instances[id] = new SignatureModal(modal, {});
        if(imageURL) SignatureModal._instances[id]._signaturePad?.fromDataURL(imageURL)
        return SignatureModal._instances[id];
    }
}